import JSZip from 'jszip';
import { collaboratorInvoicesService } from './supabaseService';
import { CollaboratorInvoice } from '../types/collaborators';

export interface ExportProgress {
  current: number;
  total: number;
  currentFileName: string;
  collaboratorName: string;
}

export interface ExportSummary {
  totalInvoices: number;
  successfulDownloads: number;
  failedDownloads: number;
  missingPDFs: number;
  collaboratorsCount: number;
  zipSizeBytes: number;
}

export interface GroupedInvoices {
  collaboratorId: string;
  collaboratorName: string;
  invoices: CollaboratorInvoice[];
}

class InvoiceExportService {
  private abortController: AbortController | null = null;

  async exportInvoicesToZip(
    startDate: Date,
    endDate: Date,
    onProgress?: (progress: ExportProgress) => void,
    onlyWithPDF: boolean = false
  ): Promise<{ blob: Blob; summary: ExportSummary } | null> {
    this.abortController = new AbortController();

    try {
      const groupedInvoices = await collaboratorInvoicesService.getByDateRangeGroupedByCollaborator(startDate, endDate);

      if (groupedInvoices.length === 0) {
        throw new Error('No se encontraron facturas en el rango de fechas seleccionado');
      }

      let allInvoices: CollaboratorInvoice[] = [];
      groupedInvoices.forEach(group => {
        allInvoices = allInvoices.concat(group.invoices);
      });

      if (onlyWithPDF) {
        allInvoices = allInvoices.filter(inv => inv.attachmentUrl && inv.attachmentUrl.trim() !== '');
      }

      const zip = new JSZip();
      let currentIndex = 0;
      let successfulDownloads = 0;
      let failedDownloads = 0;
      let missingPDFs = 0;

      for (const group of groupedInvoices) {
        const sanitizedCollaboratorName = this.sanitizeFileName(group.collaboratorName);
        const collaboratorFolder = zip.folder(sanitizedCollaboratorName);

        if (!collaboratorFolder) {
          console.error(`No se pudo crear la carpeta para ${group.collaboratorName}`);
          continue;
        }

        for (const invoice of group.invoices) {
          if (this.abortController?.signal.aborted) {
            throw new Error('Exportación cancelada por el usuario');
          }

          currentIndex++;

          if (!invoice.attachmentUrl || invoice.attachmentUrl.trim() === '') {
            missingPDFs++;
            if (onlyWithPDF) continue;

            if (onProgress) {
              onProgress({
                current: currentIndex,
                total: allInvoices.length,
                currentFileName: invoice.invoiceNumber,
                collaboratorName: group.collaboratorName
              });
            }
            continue;
          }

          try {
            const pdfBlob = await this.downloadPDF(invoice.attachmentUrl, this.abortController.signal);

            const fileName = this.generateFileName(invoice);
            collaboratorFolder.file(fileName, pdfBlob);
            successfulDownloads++;

            if (onProgress) {
              onProgress({
                current: currentIndex,
                total: allInvoices.length,
                currentFileName: fileName,
                collaboratorName: group.collaboratorName
              });
            }
          } catch (error) {
            console.error(`Error descargando factura ${invoice.invoiceNumber}:`, error);
            failedDownloads++;

            if (onProgress) {
              onProgress({
                current: currentIndex,
                total: allInvoices.length,
                currentFileName: `ERROR: ${invoice.invoiceNumber}`,
                collaboratorName: group.collaboratorName
              });
            }
          }
        }
      }

      if (successfulDownloads === 0) {
        throw new Error('No se pudo descargar ninguna factura. Verifica que las facturas tengan archivos PDF adjuntos.');
      }

      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });

      const summary: ExportSummary = {
        totalInvoices: allInvoices.length,
        successfulDownloads,
        failedDownloads,
        missingPDFs,
        collaboratorsCount: groupedInvoices.length,
        zipSizeBytes: zipBlob.size
      };

      return { blob: zipBlob, summary };

    } catch (error) {
      console.error('Error en exportInvoicesToZip:', error);
      throw error;
    } finally {
      this.abortController = null;
    }
  }

  private async downloadPDF(url: string, signal: AbortSignal): Promise<Blob> {
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, {
          signal,
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();

        if (blob.type !== 'application/pdf' && !blob.type.includes('pdf')) {
          console.warn(`Advertencia: El archivo descargado no es un PDF (${blob.type})`);
        }

        return blob;

      } catch (error) {
        lastError = error as Error;

        if (signal.aborted) {
          throw new Error('Descarga cancelada');
        }

        if (attempt < maxRetries) {
          await this.delay(1000 * attempt);
        }
      }
    }

    throw lastError || new Error('Error desconocido al descargar PDF');
  }

  private generateFileName(invoice: CollaboratorInvoice): string {
    const dateStr = invoice.date.toISOString().split('T')[0];
    const invoiceNumber = this.sanitizeFileName(invoice.invoiceNumber);

    return `${invoiceNumber}_${dateStr}.pdf`;
  }

  private sanitizeFileName(name: string): string {
    return name
      .replace(/[<>:"/\\|?*]/g, '_')
      .replace(/\s+/g, '_')
      .replace(/_+/g, '_')
      .trim();
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  cancelExport(): void {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  async getInvoicesSummary(startDate: Date, endDate: Date): Promise<{
    totalInvoices: number;
    withPDF: number;
    withoutPDF: number;
    collaborators: Array<{ name: string; count: number; withPDF: number }>;
  }> {
    try {
      const groupedInvoices = await collaboratorInvoicesService.getByDateRangeGroupedByCollaborator(startDate, endDate);

      let totalInvoices = 0;
      let withPDF = 0;
      let withoutPDF = 0;

      const collaborators = groupedInvoices.map(group => {
        const invoicesWithPDF = group.invoices.filter(inv =>
          inv.attachmentUrl && inv.attachmentUrl.trim() !== ''
        ).length;

        totalInvoices += group.invoices.length;
        withPDF += invoicesWithPDF;
        withoutPDF += (group.invoices.length - invoicesWithPDF);

        return {
          name: group.collaboratorName,
          count: group.invoices.length,
          withPDF: invoicesWithPDF
        };
      });

      return {
        totalInvoices,
        withPDF,
        withoutPDF,
        collaborators
      };
    } catch (error) {
      console.error('Error en getInvoicesSummary:', error);
      throw error;
    }
  }

  generateZipFileName(startDate: Date, endDate: Date): string {
    const start = startDate.toISOString().split('T')[0];
    const end = endDate.toISOString().split('T')[0];
    return `Facturas_${start}_a_${end}.zip`;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }
}

export const invoiceExportService = new InvoiceExportService();
